import React, { useEffect, useState } from "react";
import { Download, Mail, Pencil, Trash2, X, RotateCcw, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CheckInPanel from "@/components/CheckInPanel";
import ImportCSVPanel from "@/components/ImportCSVPanel";

const Tabs = ({ tab, setTab }) => (
  <div className="flex border-b border-slate-200 mb-8" data-testid="admin-tabs">
    {[
      { id: "attendees", label: "Attendees" },
      { id: "interests", label: "All Interests" },
      { id: "matches", label: "Mutual Matches" },
    ].map((t) => (
      <button
        key={t.id}
        data-testid={`admin-tab-${t.id}`}
        onClick={() => setTab(t.id)}
        className={`px-4 py-3 text-sm tracking-wide transition ${
          tab === t.id
            ? "border-b-2 font-semibold"
            : "text-slate-500 hover:text-slate-700"
        }`}
        style={tab === t.id ? { borderColor: "#0F172A", color: "#0F172A" } : {}}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const EditModal = ({ user, onClose, onSaved }) => {
  const [form, setForm] = useState({
    first_name: user.first_name || "",
    age: user.age || "",
    occupation: user.occupation || "",
    gender: user.gender || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/users/${user.id}`, {
        first_name: form.first_name || null,
        age: form.age ? parseInt(form.age, 10) : null,
        occupation: form.occupation || null,
        gender: form.gender || null,
      });
      toast.success("Saved");
      onSaved(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(15,23,42,0.5)" }} data-testid="admin-edit-modal">
      <div className="bg-white w-full max-w-md p-6 rounded-sm border border-slate-200">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="overline">Edit attendee</div>
            <div className="font-serif-display text-2xl">{user.email}</div>
          </div>
          <button onClick={onClose} data-testid="admin-edit-close"><X size={20} strokeWidth={1.5} /></button>
        </div>
        <div className="space-y-4">
          <input className="mm-input" placeholder="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <input className="mm-input" placeholder="Age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} />
          <input className="mm-input" placeholder="Occupation" value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} />
          <div className="grid grid-cols-2 gap-3 pt-2">
            {["male", "female"].map((g) => (
              <button key={g} type="button" onClick={() => setForm({ ...form, gender: g })} className={form.gender === g ? "mm-btn-primary" : "mm-btn-outline"} style={{ textTransform: "capitalize" }}>{g}</button>
            ))}
          </div>
        </div>
        <button onClick={save} disabled={saving} className="mm-btn-primary mt-6" data-testid="admin-edit-save">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [tab, setTab] = useState("attendees");
  const [users, setUsers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [sending, setSending] = useState(false);
  const { logout } = useAuth();
  const nav = useNavigate();

  const onLogout = () => {
    logout();
    nav("/login");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, m, i] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/matches"),
        api.get("/admin/all-interests"),
      ]);
      setUsers(u.data);
      setMatches(m.data);
      setInterests(i.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("mm_token");
      const res = await fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "attendees.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const sendEmails = async () => {
    if (!window.confirm(`Send match emails to all ${users.filter(u => u.profile_complete).length} attendees with complete profiles?`)) return;
    setSending(true);
    try {
      const res = await api.post("/admin/send-match-emails");
      toast.success(`Sent ${res.data.sent} emails (${res.data.failed} failed)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This removes all their selections too.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const resetData = async () => {
    const c1 = window.prompt("This will DELETE all attendees and all interest selections (admin account is preserved). Type 'RESET' to confirm:");
    if (c1 !== "RESET") return;
    try {
      const res = await api.delete("/admin/reset-data");
      toast.success(`Cleared ${res.data.users_deleted} attendees, ${res.data.interests_deleted} selections`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
  };

  const clearSelections = async () => {
    const c1 = window.prompt("This will wipe ALL interest selections from every attendee, but KEEP all attendees intact. Use this if people pre-selected before the event started. Type 'CLEAR' to confirm:");
    if (c1 !== "CLEAR") return;
    try {
      const res = await api.delete("/admin/clear-selections");
      toast.success(`Cleared ${res.data.interests_deleted} selections — attendees untouched`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }}>
      <header className="mm-sticky-header sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="overline">Muslim Mingle</div>
            <h1 className="font-serif-display text-2xl">Admin Dashboard</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onLogout} data-testid="admin-logout-button" className="mm-btn-outline" style={{ width: "auto", padding: "10px 18px", fontSize: 13 }}>
              <LogOut size={14} strokeWidth={1.5} className="inline mr-2" />Log out
            </button>
            <button onClick={clearSelections} data-testid="admin-clear-selections-button" className="mm-btn-outline" style={{ width: "auto", padding: "10px 18px", fontSize: 13, borderColor: "#B45309", color: "#B45309" }}>
              <RotateCcw size={14} strokeWidth={1.5} className="inline mr-2" />Clear Selections Only
            </button>
            <button onClick={resetData} data-testid="admin-reset-button" className="mm-btn-outline" style={{ width: "auto", padding: "10px 18px", fontSize: 13, borderColor: "#B91C1C", color: "#B91C1C" }}>
              <RotateCcw size={14} strokeWidth={1.5} className="inline mr-2" />Clear All Data
            </button>
            <button onClick={exportCSV} data-testid="admin-export-button" className="mm-btn-outline" style={{ width: "auto", padding: "10px 18px", fontSize: 13 }}>
              <Download size={14} strokeWidth={1.5} className="inline mr-2" />Export CSV
            </button>
            <button onClick={sendEmails} disabled={sending} data-testid="admin-trigger-matches-button" className="mm-btn-primary" style={{ width: "auto", padding: "10px 18px", fontSize: 13 }}>
              <Mail size={14} strokeWidth={1.5} className="inline mr-2" />{sending ? "Sending…" : "Send Match Emails"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 mm-fade-in">
        <ImportCSVPanel onImported={load} />
        <CheckInPanel />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <Stat label="Total attendees" value={users.length} testid="stat-total" />
          <Stat label="Completed profiles" value={users.filter(u => u.profile_complete).length} testid="stat-complete" />
          <Stat label="Mutual matches" value={matches.length} testid="stat-matches" />
        </div>

        <Tabs tab={tab} setTab={setTab} />

        {loading ? (
          <div className="overline">Loading…</div>
        ) : tab === "attendees" ? (
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <Th>First Name</Th><Th>Email</Th><Th>Age</Th><Th>Occupation</Th><Th>Gender</Th><Th>Status</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}>
                    <Td>{u.first_name || <span className="text-slate-400">—</span>}</Td>
                    <Td>{u.email}{u.is_admin && <span className="ml-2 mm-pill mm-pill-connect text-[9px]">Admin</span>}</Td>
                    <Td>{u.age || "—"}</Td>
                    <Td>{u.occupation || "—"}</Td>
                    <Td className="capitalize">{u.gender || "—"}</Td>
                    <Td>
                      <span className={u.profile_complete ? "mm-pill mm-pill-mutual" : "mm-pill mm-pill-pass"} style={{ fontSize: 9 }}>
                        {u.profile_complete ? "Complete" : "Pending"}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(u)} data-testid={`admin-edit-${u.id}`} className="p-1.5 hover:bg-slate-100"><Pencil size={14} strokeWidth={1.5} /></button>
                        <button onClick={() => deleteUser(u.id, u.email)} data-testid={`admin-delete-${u.id}`} className="p-1.5 hover:bg-red-50 text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "interests" ? (
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="admin-interests-table">
            {interests.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No selections yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <Th>Picked By</Th><Th>Email</Th><Th>→ Picked</Th><Th>Their Email</Th><Th>When</Th>
                  </tr>
                </thead>
                <tbody>
                  {interests.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-interest-row-${r.id}`}>
                      <Td><strong>{r.from.first_name}</strong> · {r.from.occupation}</Td>
                      <Td>{r.from.email}</Td>
                      <Td><strong>{r.to.first_name}</strong> · {r.to.occupation}</Td>
                      <Td>{r.to.email}</Td>
                      <Td className="text-xs text-slate-500 whitespace-nowrap">{r.updated_at ? new Date(r.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="admin-matches-table">
            {matches.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No mutual matches yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <Th>Attendee A</Th><Th>Email A</Th><Th>Attendee B</Th><Th>Email B</Th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => (
                    <tr key={`${m.user_a.id}-${m.user_b.id}`} className="border-b border-slate-100" data-testid={`admin-match-row-${m.user_a.id}-${m.user_b.id}`}>
                      <Td><strong>{m.user_a.first_name}</strong> · {m.user_a.occupation}</Td>
                      <Td>{m.user_a.email}</Td>
                      <Td><strong>{m.user_b.first_name}</strong> · {m.user_b.occupation}</Td>
                      <Td>{m.user_b.email}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>

      {editing && (
        <EditModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setUsers((all) => all.map((u) => (u.id === updated.id ? updated : u)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-slate-700 ${className}`}>{children}</td>;
const Stat = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}>
    <div className="overline">{label}</div>
    <div className="font-serif-display text-4xl mt-2">{value}</div>
  </div>
);

export default AdminPage;
